import { isBrowser, isNonEmptyString } from '@sortlist-frontend/utils';
import { getCookie } from 'cookies-next';

import { TrackingEvent } from '../events';
import { ClientEvent } from '../types/types';
import { getBrowserLocale, getGbuuid } from '../utils/utils';
import { useTrackingUuid } from './useTrackingUuid';
import { useTrackLastActivity } from './useTrackLastActivity';

export interface UseTrackProps {
  app: 'public' | 'agency' | 'client';
  /** Should be the locale shown to the user i.e. the app's language */
  locale?: string;
  /** Can be overridden e.g. in match app we use the member attributionUuid */
  userUuid?: string | null;
}

export const standaloneTrack = (
  props: Omit<UseTrackProps, 'userUuid'> & {
    /** Normalised, non-nullable user identifier */
    userUuid: string;
    sessionUuid: string;
    data: TrackingEvent;
    url: string;
    trackingEndpointUrl: string;
    onError?: (e: Error) => void;
  },
) => {
  const { app, locale, userUuid, sessionUuid, data, onError, url, trackingEndpointUrl } = props;

  const gbuuid = getGbuuid();

  const eventData: ClientEvent = {
    slug: data.slug,
    data: hasDataField(data) ? JSON.stringify(data.data) : null,
    session_uuid: sessionUuid,
    user_uuid: userUuid,
    url,
    app,
    metadata: {
      referrer: isBrowser() && isNonEmptyString(document.referrer) ? document.referrer : null,
      // session_entry_url?
      gid: getCookie('_gid')?.toString() ?? null,
      ajs_anonymous_id: getCookie('ajs_anonymous_id')?.toString() ?? null,
      ajs_user_id: getCookie('ajs_user_id')?.toString() ?? null,
      locale: getBrowserLocale() ?? null,
    },
    site_locale: locale ?? null,
    ab_tests:
      gbuuid != null
        ? {
            gbuuid,
            // This is provided for now to match the schema, but is not used anymore
            experiments: null,
          }
        : null,
  };

  if (trackingEndpointUrl == null || trackingEndpointUrl === '') {
    onError?.(new Error('trackingEndpointUrl is empty – skipping standaloneTrack'));
    return;
  }

  return (
    fetch(new URL('/api/clue', trackingEndpointUrl).toString(), {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
      method: 'POST',
      keepalive: true,
      body: JSON.stringify(eventData satisfies ClientEvent),
    })
      // This route will always return 200, so this might never get triggered
      .catch((e) => {
        onError?.(e);
        return undefined;
      })
  );
};

export const useTrack = ({ app, locale, userUuid: userUuidOverride }: UseTrackProps) => {
  const { updateLastActivity } = useTrackLastActivity();
  const { userUuid, sessionUuid } = useTrackingUuid();

  const track = (data: TrackingEvent, { onError }: { onError?: (e: Error) => void } = {}) => {
    // Update the last activity timestamp whenever an event is tracked
    updateLastActivity();

    // Use the values from useTrackingUuid directly instead of trying to read cookies again
    // This ensures we use the same UUIDs that were already synchronized in the singleton
    return standaloneTrack({
      userUuid: userUuidOverride ?? userUuid,
      sessionUuid,
      data,
      onError,
      app,
      locale,
      trackingEndpointUrl: isBrowser() ? window.location.origin : '',
      url: isBrowser() ? window.location.href : '',
    });
  };

  return {
    track,
  };
};

const hasDataField = (event: TrackingEvent): event is TrackingEvent & { data: unknown } => {
  return 'data' in event;
};
